// TJBA 問い合わせ・資料請求フォームの送信先 (GAS WebApp)
// scriptId: 1Aj2PJI62fKh7eRzjRgBqSt6HZ0_I4Ag_9pvm2-ruHNaKe2usRrGLx926 (owner: y.ishii@thespectre.jp)
// 匿名アクセス可の稼働デプロイ (v1)。HEADデプロイのURLはGoogleログイン必須のため使用しない。
// GASはCORSプリフライトに応答できないため、Content-Type は text/plain でJSON文字列を送る。
export const CONTACT_ENDPOINT =
  'https://script.google.com/macros/s/AKfycbzVHmPZjel12QonGpVipBYlC0qHjTNfKZqb43GwMun4sOzxxXTKkv0FIBxws_dMsrhY/exec';

export const CONTACT_HEADERS = { 'Content-Type': 'text/plain;charset=utf-8' };

export type ContactLang = 'ja' | 'en';

// LanguageContext と同一規則: /en 配下なら 'en'（URLが言語の唯一のソース）
export function detectContactLang(pathname: string = window.location.pathname): ContactLang {
  return pathname === '/en' || pathname.startsWith('/en/') ? 'en' : 'ja';
}

// フォームの画面遷移用: /en 配下で操作中なら遷移先にも /en を付ける(英語の利用者を日本語ページへ移さない)。
export function localizedPath(path: string): string {
  return detectContactLang() === 'en' ? `/en${path}` : path;
}

// メディア(取材・メディア関連)のお問い合わせに添付できる資料。受信側 gas/Code.gs buildAttachments_ と同じ上限・形式。
// Yuichi 2026-10-08「メディアからの問い合わせはファイル添付できるように全サイトやってね」
export const MEDIA_ATTACH_ACCEPT = '.pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.jpg,.jpeg,.png,.heic';
const MEDIA_ATTACH_EXT = /\.(pdf|docx?|pptx?|xlsx?|jpe?g|png|heic)$/i;

export function checkMediaAttachments(files: File[]): string {
  const en = detectContactLang() === 'en';
  if (files.length > 3) return en ? 'You can attach up to 3 files.' : '添付できるファイルは3点までです。';
  const bad = files.find((f) => !MEDIA_ATTACH_EXT.test(f.name));
  if (bad) {
    return en
      ? `"${bad.name}" cannot be attached. Please use PDF, Word, PowerPoint, Excel, JPEG, PNG or HEIC.`
      : `「${bad.name}」は添付できない形式です。PDF・Word・PowerPoint・Excel・JPEG・PNG・HEIC でお送りください。`;
  }
  if (files.reduce((s, f) => s + f.size, 0) > 10 * 1024 * 1024) {
    return en ? 'Attachments must total 10MB or less.' : '添付ファイルは合計10MBまでです。';
  }
  return '';
}

export async function filesToAttachments(files: File[]): Promise<{ name: string; mimeType: string; data: string }[]> {
  const read = (f: File) => new Promise<string>((resolve, reject) => {
    const r = new FileReader();
    r.onload = () => {
      const s = String(r.result ?? '');
      const i = s.indexOf(',');
      resolve(i >= 0 ? s.slice(i + 1) : s);
    };
    r.onerror = () => reject(r.error ?? new Error('file read failed'));
    r.readAsDataURL(f);
  });
  const out = [];
  for (const f of files) out.push({ name: f.name, mimeType: f.type || 'application/octet-stream', data: await read(f) });
  return out;
}

export function contactSubmissionErrorMessage(): string {
  return detectContactLang() === 'en'
    ? 'We could not send your message. Please try again later or email info@butler-academy.jp.'
    : '送信できませんでした。恐れ入りますが、時間をおいて再度お試しいただくか、info@butler-academy.jp までご連絡ください。';
}

// 全フォーム共通: GAS が受付成功を返した場合だけ resolve する。
// HTTP 200 でも status:error や JSON 以外の応答は失敗として扱う。
export async function submitContact(
  formType: string,
  data: object,
): Promise<void> {
  const response = await fetch(CONTACT_ENDPOINT, {
    method: 'POST',
    headers: CONTACT_HEADERS,
    body: JSON.stringify({
      ...data,
      formType,
      lang: detectContactLang(),
      _hp: '',
      source: window.location.href,
    }),
  });
  if (!response.ok) throw new Error('Contact request failed');

  const result: unknown = await response.json();
  if (
    typeof result !== 'object' ||
    result === null ||
    !('status' in result) ||
    result.status !== 'ok'
  ) {
    throw new Error('Contact submission was not accepted');
  }
}
